/**
 * Accessible Navigation Module
 * 
 * Provides keyboard navigation and WCAG compliance for dropdown menus
 * Supports Tab, Enter, Space, and Escape keys
 */

export const accessibleNavigation = (() => {
	let activeDropdown = null;

	const init = () => {
		// Find all dropdown triggers
		const triggers = document.querySelectorAll('[data-dropdown-trigger]');
		
		if (!triggers.length) return;

		triggers.forEach(trigger => {
			const dropdownId = trigger.getAttribute('data-dropdown-trigger');
			
			// Click event for mobile
			trigger.addEventListener('click', (e) => {
				handleTriggerClick(e, trigger, dropdownId);
			});

			// Keyboard events
			trigger.addEventListener('keydown', (e) => {
				handleKeyDown(e, trigger, dropdownId);
			});

			// Desktop: Open on focus (for keyboard users)
			trigger.addEventListener('focus', (e) => {
				if (window.innerWidth >= 1024) { // lg breakpoint
					openDropdown(trigger, dropdownId);
				}
			});
		});

		// Close dropdown when clicking outside
		document.addEventListener('click', (e) => {
			if (!e.target.closest('[data-dropdown-trigger]') && 
				!e.target.closest('[data-dropdown-content]')) {
				closeAllDropdowns();
			}
		});

		// Close dropdown on Escape key
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape' && activeDropdown) {
				closeAllDropdowns();
				// Return focus to the trigger
				const trigger = document.querySelector(`[data-dropdown-trigger="${activeDropdown}"]`);
				if (trigger) trigger.focus();
			}
		});
	};

	const handleTriggerClick = (e, trigger, dropdownId) => {
		e.preventDefault();
		e.stopPropagation();
		
		const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
		
		if (isExpanded) {
			closeDropdown(trigger, dropdownId);
		} else {
			// Close other dropdowns first
			closeAllDropdowns();
			openDropdown(trigger, dropdownId);
		}
	};

	const handleKeyDown = (e, trigger, dropdownId) => {
		// Handle Enter and Space keys
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			handleTriggerClick(e, trigger, dropdownId);
		}
		
		// Handle Escape key
		if (e.key === 'Escape') {
			closeDropdown(trigger, dropdownId);
		}

		// Handle Arrow Down - open menu and focus first item
		if (e.key === 'ArrowDown') {
			e.preventDefault();
			openDropdown(trigger, dropdownId);
			
			// Focus first link in dropdown
			setTimeout(() => {
				const dropdown = getDropdownElement(dropdownId);
				const firstLink = dropdown?.querySelector('a');
				if (firstLink) firstLink.focus();
			}, 100);
		}
	};

	const openDropdown = (trigger, dropdownId) => {
		// Update ARIA attributes
		trigger.setAttribute('aria-expanded', 'true');

		// Show dropdown content
		const dropdownContents = document.querySelectorAll(`[data-dropdown-content="${dropdownId}"]`);
		dropdownContents.forEach(content => {
			if (content.hasAttribute('data-dropdown-mobile')) {
				// Mobile dropdown
				content.classList.remove('hidden');
				content.classList.add('block');
			} else {
				// Desktop dropdown - visibility handled by CSS (hover/focus-within)
				// Just ensure it's accessible
			}
		});

		activeDropdown = dropdownId;
	};

	const closeDropdown = (trigger, dropdownId) => {
		// Update ARIA attributes
		trigger.setAttribute('aria-expanded', 'false');

		// Hide dropdown content
		const dropdownContents = document.querySelectorAll(`[data-dropdown-content="${dropdownId}"]`);
		dropdownContents.forEach(content => {
			if (content.hasAttribute('data-dropdown-mobile')) {
				content.classList.add('hidden');
				content.classList.remove('block');
			}
		});

		if (activeDropdown === dropdownId) {
			activeDropdown = null;
		}
	};

	const closeAllDropdowns = () => {
		const triggers = document.querySelectorAll('[data-dropdown-trigger]');
		triggers.forEach(trigger => {
			const dropdownId = trigger.getAttribute('data-dropdown-trigger');
			closeDropdown(trigger, dropdownId);
		});
	};

	const getDropdownElement = (dropdownId) => {
		return document.querySelector(`[data-dropdown-content="${dropdownId}"]:not([data-dropdown-mobile])`);
	};

	return {
		init,
	};
})();
